/**
 * mobile-segments-sheet.tsx — Hộp "Sửa phân đoạn" (trượt từ đáy) của Hộp thư di động: bật / tắt từng phân đoạn,
 * đổi thứ tự bằng KÉO-THẢ (tay cầm bên trái, ngón tay / chuột) hoặc nút lên / xuống (bàn phím, trợ năng), thêm phân đoạn theo Nhãn;
 * luôn giữ tối thiểu một. Lưu ngay mỗi lần đổi (theo người dùng, đồng bộ nhiều thiết bị — hooks/use-mobile-segments.ts).
 * Kéo-thả tự viết bằng pointer events (`touch-action: none` CHỈ trên tay cầm → vẫn cuộn được hộp), tự cuộn khi kéo sát mép.
 * Logic thuần: mobile-segments.ts, lib/reorder-drag.ts.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, Check, GripVertical, RotateCcw, Tag } from 'lucide-react'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { cn } from '@/lib/utils'
import { autoScrollDelta, clampDragDy, dropIndex, dropLineY, moveTo, type RowBox } from '@/lib/reorder-drag'
import type { TagDef } from '@/hooks/use-settings'
import {
  DEFAULT_SEGMENTS, SEGMENT_CATALOG, editorOrder, moveSegment, normalizeSegments, segmentLabel, tagOfSegment, toggleSegment,
  type SegmentId,
} from './mobile-segments'

export function SegmentsSheet({ open, onOpenChange, segments, onChange, tags }: {
  open: boolean
  onOpenChange: (v: boolean) => void
  segments: SegmentId[]
  onChange: (next: SegmentId[]) => void
  tags: TagDef[]
}) {
  const { t } = useTranslation('convList')
  const order = editorOrder(segments, tags.map((tg) => tg.name))
  const drag = useDragReorder(segments, onChange)
  const hintOf = (id: SegmentId) => SEGMENT_CATALOG.find((d) => d.filter === id)?.hint ?? t('seg.tagHint')
  const colorOf = (id: SegmentId) => tags.find((tg) => tg.name === tagOfSegment(id))?.color
  const onlyOne = segments.length <= 1
  const isDefault = segments.length === DEFAULT_SEGMENTS.length && segments.every((x, i) => x === DEFAULT_SEGMENTS[i])
  // Chỉ mục đầu của khối đang tắt — để tách "Đang hiển thị" / "Thêm vào thanh".
  const firstOff = order.findIndex((id) => !segments.includes(id))

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={t('segSheet.title')}>
        <div className="pb-3 pt-2">
          <div className="flex items-center justify-between px-4 pb-1">
            <h2 className="text-base font-semibold">{t('segSheet.title')}</h2>
            <button
              type="button"
              onClick={() => onChange(normalizeSegments(null))}
              disabled={isDefault}
              className="inline-flex h-9 items-center gap-1 rounded-full px-2.5 text-sm text-muted-foreground active:bg-muted disabled:opacity-40"
            >
              <RotateCcw className="h-3.5 w-3.5" /> {t('shared.default')}
            </button>
          </div>
          <p className="px-4 pb-2 text-xs text-muted-foreground">
            {t('segSheet.intro')}
          </p>
          <ul ref={drag.listRef} className="relative">
            {order.map((id, i) => {
              const on = segments.includes(id)
              const pos = segments.indexOf(id)
              const dragging = drag.active?.id === id
              return (
                <li
                  key={id}
                  ref={drag.rowRef(id)}
                  className={cn(dragging && 'relative z-10 bg-background shadow-lg ring-1 ring-primary/30')}
                  style={dragging ? { transform: `translateY(${drag.active!.dy}px)` } : undefined}
                >
                  {i === firstOff && firstOff > 0 && <p className="bg-muted/50 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t('segSheet.addToBar')}</p>}
                  <div className="flex items-center gap-1 pr-2">
                    {on ? (
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={t('segSheet.dragAria', { label: segmentLabel(id) })}
                        {...drag.grip(id)}
                        // touch-action: none CHỈ ở tay cầm — vùng còn lại của hộp vẫn cuộn bằng ngón tay.
                        style={{ touchAction: 'none' }}
                        className={cn('ml-2 flex h-11 w-9 shrink-0 cursor-grab items-center justify-center rounded-full text-muted-foreground active:cursor-grabbing', dragging && 'text-primary')}
                      >
                        <GripVertical className="h-5 w-5" />
                      </button>
                    ) : <span className="ml-2 w-9 shrink-0" aria-hidden />}
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      aria-label={on ? t('segSheet.hideAria', { label: segmentLabel(id) }) : t('segSheet.showAria', { label: segmentLabel(id) })}
                      disabled={on && onlyOne}
                      onClick={() => onChange(toggleSegment(segments, id))}
                      className="flex min-h-[52px] min-w-0 flex-1 items-center gap-3 pr-2 text-left active:bg-muted disabled:opacity-60"
                    >
                      <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2', on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40')}>
                        {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 text-[15px] font-medium">
                          {tagOfSegment(id) !== undefined && <Tag className="h-3.5 w-3.5 shrink-0" style={{ color: colorOf(id) }} />}
                          <span className="truncate">{segmentLabel(id)}</span>
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">{hintOf(id)}</span>
                      </span>
                    </button>
                    {on && (
                      <>
                        <button type="button" aria-label={t('segSheet.moveUp', { label: segmentLabel(id) })} disabled={pos === 0} onClick={() => onChange(moveSegment(segments, id, -1))} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground active:bg-muted disabled:opacity-25">
                          <ArrowUp className="h-5 w-5" />
                        </button>
                        <button type="button" aria-label={t('segSheet.moveDown', { label: segmentLabel(id) })} disabled={pos === segments.length - 1} onClick={() => onChange(moveSegment(segments, id, 1))} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground active:bg-muted disabled:opacity-25">
                          <ArrowDown className="h-5 w-5" />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              )
            })}
            {/* Vạch chỉ chỗ sẽ thả */}
            {drag.active && drag.active.to !== drag.active.from && (
              <li aria-hidden data-testid="drop-line" className="pointer-events-none absolute inset-x-4 z-20 h-0.5 -translate-y-px rounded-full bg-primary" style={{ top: drag.active.lineY }} />
            )}
          </ul>
          {tags.length === 0 && <p className="px-4 pt-2 text-xs text-muted-foreground">{t('segSheet.noTags')}</p>}
          <div className="px-4 pt-3">
            <button type="button" onClick={() => onOpenChange(false)} className="h-11 w-full rounded-full bg-primary text-[15px] font-medium text-primary-foreground active:opacity-90">{t('common:actions.done')}</button>
          </div>
        </div>
      </BottomSheetContent>
    </BottomSheet>
  )
}

/** Khung cuộn gần nhất của một phần tử (khung cuộn của hộp). */
function scrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let n = el?.parentElement ?? null; n; n = n.parentElement) {
    const o = getComputedStyle(n).overflowY
    if (o === 'auto' || o === 'scroll') return n
  }
  return null
}

interface ActiveDrag { id: SegmentId; from: number; to: number; dy: number; lineY: number }

interface Session { pid: number; id: SegmentId; from: number; boxes: RowBox[]; startY: number; lastClientY: number; raf: number; scroller: HTMLElement | null }

/**
 * Kéo-thả sắp xếp các hàng phân đoạn ĐANG BẬT (nằm đầu danh sách). Đo hộp các hàng một lần lúc bắt đầu kéo; hàng đang kéo
 * chạy theo ngón tay (translateY), vạch chỉ chỗ thả cập nhật liên tục, thả tay → `onChange(moveTo(...))`.
 * Toạ độ tính theo mép trên danh sách (`getBoundingClientRect` đã gồm phần cuộn) nên tự cuộn không làm lệch.
 */
export function useDragReorder(segments: SegmentId[], onChange: (next: SegmentId[]) => void) {
  const listRef = useRef<HTMLUListElement>(null)
  const rows = useRef(new Map<SegmentId, HTMLLIElement>())
  const [active, setActive] = useState<ActiveDrag | null>(null)
  const session = useRef<Session | null>(null)
  const last = useRef<ActiveDrag | null>(null)
  const latest = useRef({ segments, onChange })
  latest.current = { segments, onChange }

  useEffect(() => () => { if (session.current) cancelAnimationFrame(session.current.raf); session.current = null }, [])

  const listTop = () => listRef.current?.getBoundingClientRect().top ?? 0

  function update(clientY: number) {
    const s = session.current
    if (!s) return
    s.lastClientY = clientY
    // Kẹp trong khối hàng kéo được (hàng dịch ra ngoài làm vùng cuộn phình ra → tự cuộn chạy mãi).
    const blockTop = Math.min(...s.boxes.map((b) => b.top))
    const blockBottom = Math.max(...s.boxes.map((b) => b.top + b.height))
    const rawDy = clientY - listTop() - s.startY
    const dy = clampDragDy(s.boxes[s.from], blockTop, blockBottom, rawDy)
    // Chỉ số thả tính theo độ dời THÔ (kéo quá đầu / cuối khối vẫn là "thả đầu / cuối"); hàng chỉ HIỂN THỊ ở độ dời đã kẹp.
    const to = dropIndex(s.boxes.map((b) => b.top + b.height / 2), s.from, s.boxes[s.from].top + s.boxes[s.from].height / 2 + rawDy)
    const a = { id: s.id, from: s.from, to, dy, lineY: dropLineY(s.boxes, s.from, to) }
    last.current = a
    setActive(a)
  }

  function tick() {
    const s = session.current
    if (!s) return
    if (s.scroller) {
      const r = s.scroller.getBoundingClientRect()
      const d = autoScrollDelta(s.lastClientY, r.top, r.bottom)
      if (d !== 0) { s.scroller.scrollTop += d; update(s.lastClientY) }
    }
    s.raf = requestAnimationFrame(tick)
  }

  function finish(commit: boolean) {
    const s = session.current
    if (!s) return
    cancelAnimationFrame(s.raf)
    session.current = null
    const a = last.current
    last.current = null
    setActive(null)
    if (commit && a) {
      const next = moveTo(latest.current.segments, a.from, a.to)
      if (next !== latest.current.segments) latest.current.onChange(next)
    }
  }

  return {
    listRef,
    active,
    rowRef: (id: SegmentId) => (el: HTMLLIElement | null) => { if (el) rows.current.set(id, el); else rows.current.delete(id) },
    /** Thuộc tính cho tay cầm của hàng `id`. */
    grip: (id: SegmentId) => ({
      onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
        if (session.current || (e.pointerType === 'mouse' && e.button !== 0)) return
        const from = latest.current.segments.indexOf(id)
        if (from < 0 || latest.current.segments.length < 2) return
        const boxes: RowBox[] = latest.current.segments.map((sid) => {
          const li = rows.current.get(sid)
          return { top: li?.offsetTop ?? 0, height: li?.offsetHeight ?? 0 }
        })
        e.preventDefault()
        try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* bỏ qua */ }
        session.current = {
          pid: e.pointerId, id, from, boxes, startY: e.clientY - listTop(), lastClientY: e.clientY, raf: 0,
          scroller: scrollParent(listRef.current),
        }
        session.current.raf = requestAnimationFrame(tick)
        update(e.clientY)
      },
      onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
        if (session.current && e.pointerId === session.current.pid) update(e.clientY)
      },
      onPointerUp: (e: React.PointerEvent<HTMLElement>) => {
        if (session.current && e.pointerId === session.current.pid) finish(true)
      },
      onPointerCancel: () => finish(false),
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(), // nhấn giữ trên Android không bật menu
    }),
  }
}
